import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Plus, RotateCcw, Save, Trash2, X } from 'lucide-react';
import apiClient from '../../api/client';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { CategoryPicker } from '../../components/CategoryPicker';
import { cn } from '../../utils/cn';
import {
  COUNTER_ICONS,
  columnTitle,
  layoutCodes,
  tabLabel,
  type CounterColumn,
  type CounterIcon,
  type CounterLayout,
  type CounterTab,
  type StoredCounterLayout,
} from '../../components/order/counterTabs';

interface CatInfo {
  code: string;
  name: string;
  count: number;
}

const move = <T,>(list: T[], i: number, by: -1 | 1): T[] => {
  const j = i + by;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

/**
 * Settings → Counter tabs (migration 148): the tabs a seller sees on a new sale before typing a
 * reference ("Pièces courantes", "Pneus", "Huiles & liquides", "Forfaits"…), and the product
 * categories behind each one. Every entry is picked from the catalogue tree — a TecDoc category,
 * a universal group or one of our own — and shows the catalogue's name and how many of our
 * products it holds today. Nothing changes at the counter until "Save".
 */
export default function CounterTabs() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [confirm, confirmNode] = useConfirm();
  const [stored, setStored] = useState<StoredCounterLayout | null>(null);
  const [layout, setLayout] = useState<CounterLayout | null>(null);
  const [sel, setSel] = useState(0);
  const [cats, setCats] = useState<Map<string, CatInfo>>(new Map());
  const [picking, setPicking] = useState<number | null>(null); // column index
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await apiClient.get<StoredCounterLayout>('/catalog/counter-tabs');
      setStored(data);
      setLayout(data.layout);
      setSel(0);
    } catch (err) {
      showApiError(err);
    }
  };
  useEffect(() => {
    void load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Names and counts for every code in the layout (new ones are fetched as they are added).
  const codes = useMemo(() => (layout ? layoutCodes(layout) : []), [layout]);
  useEffect(() => {
    const missing = codes.filter((c) => !cats.has(c));
    if (!missing.length) return;
    apiClient
      .get<CatInfo[]>(
        `/catalog/browse-categories?codes=${missing.map(encodeURIComponent).join(',')}`
      )
      .then((r) =>
        setCats((m) => {
          const next = new Map(m);
          for (const c of r.data) next.set(c.code, c);
          return next;
        })
      )
      .catch(() => undefined);
  }, [codes]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = !!stored && !!layout && JSON.stringify(stored.layout) !== JSON.stringify(layout);
  if (!layout)
    return <PageHeader title={t('counterTabs.title')} description={t('counterTabs.description')} />;

  const tab = layout.tabs[sel];
  const setTabs = (tabs: CounterTab[]) => setLayout({ tabs });
  const patchTab = (patch: Partial<CounterTab>) =>
    setTabs(layout.tabs.map((x, i) => (i === sel ? { ...x, ...patch } : x)));
  const patchColumns = (columns: CounterColumn[]) => patchTab({ columns });

  const addTab = () => {
    const next: CounterTab = {
      id: `tab-${Date.now().toString(36)}`,
      label: t('counterTabs.newTabName'),
      labelKey: null,
      icon: 'list',
      columns: [{ title: null, titleKey: null, codes: [] }],
    };
    setTabs([...layout.tabs, next]);
    setSel(layout.tabs.length);
  };
  const removeTab = async () => {
    const ok = await confirm({
      title: t('counterTabs.removeTabTitle', { name: tabLabel(t, tab) }),
      description: t('counterTabs.removeTabBody'),
      confirmLabel: t('counterTabs.remove'),
      tone: 'danger',
    });
    if (!ok) return;
    setTabs(layout.tabs.filter((_, i) => i !== sel));
    setSel(Math.max(0, sel - 1));
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.put<StoredCounterLayout>('/catalog/counter-tabs', layout);
      setStored(data);
      setLayout(data.layout);
      push({ tone: 'success', message: t('counterTabs.saved') });
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };
  const reset = async () => {
    const ok = await confirm({
      title: t('counterTabs.resetTitle'),
      description: t('counterTabs.resetBody'),
      confirmLabel: t('counterTabs.reset'),
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await apiClient.delete<StoredCounterLayout>('/catalog/counter-tabs');
      setStored(data);
      setLayout(data.layout);
      setSel(0);
      push({ tone: 'success', message: t('counterTabs.resetDone') });
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t('counterTabs.title')}
        description={t('counterTabs.description')}
        actions={
          <div className="flex gap-2">
            {stored?.custom && (
              <Button variant="outline" size="sm" onClick={() => void reset()} disabled={busy}>
                <RotateCcw className="h-4 w-4" /> {t('counterTabs.reset')}
              </Button>
            )}
            <Button size="sm" onClick={() => void save()} disabled={busy || !dirty}>
              <Save className="h-4 w-4" /> {t('counterTabs.save')}
            </Button>
          </div>
        }
      />
      <p className="mb-4 text-xs text-muted-foreground">
        {stored?.custom
          ? t('counterTabs.customSince', {
              who: stored.updatedBy ?? '—',
              when: stored.updatedAt ? new Date(stored.updatedAt).toLocaleString() : '—',
            })
          : t('counterTabs.isDefault')}
        {dirty && <span className="ml-2 font-medium text-warning">{t('counterTabs.unsaved')}</span>}
      </p>

      <div className="grid gap-4 md:grid-cols-[260px_minmax(0,1fr)]">
        {/* the tabs, in counter order */}
        <section className="rounded-lg border bg-card">
          <header className="border-b px-3 py-2 text-sm font-semibold">
            {t('counterTabs.tabs')}
          </header>
          <ul className="divide-y">
            {layout.tabs.map((x, i) => {
              const Icon = COUNTER_ICONS[x.icon] ?? COUNTER_ICONS.list;
              return (
                <li
                  key={x.id}
                  className={cn('flex items-center gap-1 px-2 py-1.5', i === sel && 'bg-accent/60')}
                >
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm"
                    onClick={() => setSel(i)}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{tabLabel(t, x) || '—'}</span>
                  </button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    aria-label={t('counterTabs.moveUp')}
                    disabled={i === 0}
                    onClick={() => {
                      setTabs(move(layout.tabs, i, -1));
                      setSel(i - 1);
                    }}
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    aria-label={t('counterTabs.moveDown')}
                    disabled={i === layout.tabs.length - 1}
                    onClick={() => {
                      setTabs(move(layout.tabs, i, 1));
                      setSel(i + 1);
                    }}
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                </li>
              );
            })}
          </ul>
          <div className="border-t p-2">
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              onClick={addTab}
              disabled={layout.tabs.length >= 8}
            >
              <Plus className="h-4 w-4" /> {t('counterTabs.addTab')}
            </Button>
          </div>
        </section>

        {/* the selected tab */}
        {tab ? (
          <section className="space-y-4 rounded-lg border bg-card p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[14rem] flex-1">
                <label className="mb-1 block text-xs font-medium" htmlFor="ct-name">
                  {t('counterTabs.tabName')}
                </label>
                <Input
                  id="ct-name"
                  value={tab.label ?? ''}
                  placeholder={tab.labelKey ? t(`counter.browseTabs.${tab.labelKey}`) : ''}
                  maxLength={40}
                  onChange={(e) => patchTab({ label: e.target.value || null })}
                />
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void removeTab()}
                disabled={layout.tabs.length <= 1}
              >
                <Trash2 className="h-4 w-4" /> {t('counterTabs.removeTab')}
              </Button>
            </div>

            <div>
              <span className="mb-1 block text-xs font-medium">{t('counterTabs.icon')}</span>
              <div className="flex flex-wrap gap-1">
                {(Object.keys(COUNTER_ICONS) as CounterIcon[]).map((k) => {
                  const Icon = COUNTER_ICONS[k];
                  return (
                    <button
                      key={k}
                      type="button"
                      aria-label={k}
                      aria-pressed={tab.icon === k}
                      onClick={() => patchTab({ icon: k })}
                      className={cn(
                        'rounded-md border p-2',
                        tab.icon === k ? 'border-primary bg-primary/10' : 'hover:bg-accent'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </button>
                  );
                })}
              </div>
            </div>

            {tab.columns.length === 0 && (
              <p className="rounded-md border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
                {t('counterTabs.noColumns')}
              </p>
            )}

            <div className="grid gap-3 lg:grid-cols-2">
              {tab.columns.map((col, ci) => (
                <div key={ci} className="rounded-md border">
                  <div className="flex items-center gap-1 border-b px-2 py-1.5">
                    <Input
                      className="h-8"
                      value={col.title ?? ''}
                      placeholder={
                        columnTitle(t, { ...col, title: null }) || t('counterTabs.columnTitle')
                      }
                      maxLength={40}
                      aria-label={t('counterTabs.columnTitle')}
                      onChange={(e) =>
                        patchColumns(
                          tab.columns.map((c, i) =>
                            i === ci ? { ...c, title: e.target.value || null } : c
                          )
                        )
                      }
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      aria-label={t('counterTabs.moveLeft')}
                      disabled={ci === 0}
                      onClick={() => patchColumns(move(tab.columns, ci, -1))}
                    >
                      <ArrowUp className="h-3.5 w-3.5 -rotate-90" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      aria-label={t('counterTabs.moveRight')}
                      disabled={ci === tab.columns.length - 1}
                      onClick={() => patchColumns(move(tab.columns, ci, 1))}
                    >
                      <ArrowDown className="h-3.5 w-3.5 -rotate-90" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      aria-label={t('counterTabs.removeColumn')}
                      onClick={() => patchColumns(tab.columns.filter((_, i) => i !== ci))}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <ul className="divide-y">
                    {col.codes.map((code, k) => {
                      const c = cats.get(code);
                      return (
                        <li key={code} className="flex items-center gap-1 px-2 py-1 text-sm">
                          <div className="min-w-0 flex-1">
                            <div className="truncate">{c?.name ?? code}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {c
                                ? t('counterTabs.productCount', { count: c.count })
                                : t('counterTabs.unknownCode')}
                              {' · '}
                              <span className="font-mono">{code}</span>
                            </div>
                          </div>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            aria-label={t('counterTabs.moveUp')}
                            disabled={k === 0}
                            onClick={() =>
                              patchColumns(
                                tab.columns.map((x, i) =>
                                  i === ci ? { ...x, codes: move(x.codes, k, -1) } : x
                                )
                              )
                            }
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            aria-label={t('counterTabs.moveDown')}
                            disabled={k === col.codes.length - 1}
                            onClick={() =>
                              patchColumns(
                                tab.columns.map((x, i) =>
                                  i === ci ? { ...x, codes: move(x.codes, k, 1) } : x
                                )
                              )
                            }
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            aria-label={t('counterTabs.removeCategory')}
                            onClick={() =>
                              patchColumns(
                                tab.columns.map((x, i) =>
                                  i === ci ? { ...x, codes: x.codes.filter((y) => y !== code) } : x
                                )
                              )
                            }
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="p-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setPicking(ci)}
                      disabled={col.codes.length >= 40}
                    >
                      <Plus className="h-4 w-4" /> {t('counterTabs.addCategory')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <Button
              size="sm"
              variant="outline"
              disabled={tab.columns.length >= 6}
              onClick={() =>
                patchColumns([...tab.columns, { title: null, titleKey: null, codes: [] }])
              }
            >
              <Plus className="h-4 w-4" /> {t('counterTabs.addColumn')}
            </Button>
          </section>
        ) : (
          <section className="rounded-lg border p-6 text-sm text-muted-foreground">
            {t('counterTabs.noTabs')}
          </section>
        )}
      </div>

      {picking !== null && tab && (
        <CategoryPicker
          onClose={() => setPicking(null)}
          onPick={(_id, _name, code) => {
            const ci = picking;
            setPicking(null);
            if (!code) return;
            patchColumns(
              tab.columns.map((x, i) =>
                i === ci && !x.codes.includes(code) ? { ...x, codes: [...x.codes, code] } : x
              )
            );
          }}
        />
      )}
      {confirmNode}
    </div>
  );
}
